import {
  ArrowRight,
  BookOpen,
  CalendarCheck,
  CheckCircle2,
  Circle,
  Clock3,
  Compass,
  FileText,
  FolderGit2,
  GraduationCap,
  MessagesSquare,
  Sparkles,
  Target,
  Trophy,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  completeStudentGrowthTask,
  getGrowthEventsAbilityScore,
  getGrowthEventsTodaySuggestion,
  getGrowthEventsWeeklyReport,
  getGrowthMentorToday,
  getLatestAiGrowthPlan,
  getStudentGrowthDashboard,
  getStudentGrowthMe,
  getStudentGrowthToday,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { openFeedbackDialog } from '../feedback-events.js'
import {
  buildStudentRoadmap,
  getCompletedRoadmapStages,
  getRoadmapSummary,
} from '../student-roadmap.js'
import { getCareerDirection, getEffectiveGrowthPlan, hasEffectiveGrowthData } from '../student-growth-state.js'

function dateTime(value, language) {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed.toLocaleString(language === 'en' ? 'en-US' : 'zh-CN', { hour12: false })
}

function normalizeTasks(payload, t) {
  const unified = Array.isArray(payload?.tasks) ? payload.tasks : []
  return unified.map((task) => ({
    id: String(task.id),
    title: task.title || task.content || t('growthCenter.taskFallbackTitle'),
    type: task.type || task.category || 'learning',
    status: task.status || 'pending',
    createdAt: task.createdAt || task.created_at || null,
    updatedAt: task.updatedAt || task.updated_at || null,
  }))
}

function normalizeEvents(payload, t) {
  const events = Array.isArray(payload?.events) ? payload.events : []
  return events.map((event) => ({
    id: String(event.id || event.eventId || event.createdAt || event.created_at || event.title),
    title: event.title || t('growthCenter.eventFallbackTitle'),
    description: event.description || '',
    type: event.eventType || event.event_type || 'growth',
    createdAt: event.createdAt || event.created_at || event.updatedAt || event.updated_at || null,
  }))
}

function normalizeGrowthLevel(payload, fallback) {
  const level = payload?.level
  if (level && typeof level === 'object') {
    return level.level ?? level.value ?? level.label ?? fallback
  }
  return level ?? fallback
}

function normalizeGrowthValue(payload, fallback) {
  return payload?.stats?.growthValue
    ?? payload?.stats?.growth_value
    ?? payload?.stats?.score
    ?? payload?.stats?.progress
    ?? fallback
}

function taskTypeLabel(type, t) {
  if (type === 'project') return t('growthCenter.project')
  if (type === 'resume') return t('growthCenter.resume')
  if (type === 'interview') return t('growthCenter.interview')
  return t('growthCenter.learning')
}

function mentorStatusKey(status) {
  if (status === 'active') return 'growthMentor.statusActive'
  if (status === 'slowing') return 'growthMentor.statusSlowing'
  return 'growthMentor.statusQuiet'
}

function weakPointLabel(item, t) {
  if (!item) return ''
  if (item.type === 'practice') return `${t('growthMentor.weakPractice')} - ${item.score || 0}`
  if (item.type === 'learning') return `${t('growthMentor.weakLearning')} - ${item.score || 0}%`
  if (item.type === 'project') return `${t('growthMentor.weakProject')} - ${item.score || 0}%`
  return item.title || ''
}

function buildFallbackTasks(activePlan, t) {
  if (!activePlan) return []
  const target = activePlan.targetDirection || activePlan.target_direction || activePlan.direction || t('growthCenter.currentTarget')
  return [
    {
      id: 'fallback-learning',
      type: 'learning',
      title: t('growthCenter.fallbackLearningTask', { target }),
      status: 'pending',
      readonly: true,
    },
    {
      id: 'fallback-project',
      type: 'project',
      title: t('growthCenter.fallbackProjectTask'),
      status: 'pending',
      readonly: true,
    },
    {
      id: 'fallback-resume',
      type: 'resume',
      title: t('growthCenter.fallbackResumeTask'),
      status: 'pending',
      readonly: true,
    },
  ]
}

function readLocalPlanItems(key) {
  if (typeof window === 'undefined') return []
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || '[]')
    return Array.isArray(parsed) ? parsed.filter((item) => item && item.id) : []
  } catch {
    return []
  }
}

function EmptyGrowthState() {
  const navigate = useNavigate()
  const { t } = useLanguage()
  return (
    <main className="page-container student-growth-center-page">
      <section className="student-growth-daily-assistant">
        <div>
          <span><Sparkles size={18} /> {t('growthCenter.emptyEyebrow')}</span>
          <h1>{t('growthCenter.emptyTitle')}</h1>
          <p>{t('growthCenter.emptyDesc')}</p>
        </div>
        <div className="student-growth-daily-meta">
          <article><Clock3 size={16} /><span>{t('growthCenter.aboutThreeMinutes')}</span></article>
          <article><Trophy size={16} /><span>{t('growthCenter.beginnerFirst')}</span></article>
          <article><CalendarCheck size={16} /><span>{t('growthCenter.dailyTasks')}</span></article>
        </div>
        <button
          type="button"
          className="primary-button"
          onClick={() => navigate('/explore')}
        >
          {t('growthCenter.generateChangePlan')} <ArrowRight size={16} />
        </button>
        <Link className="secondary-button" to="/projects">
          <FolderGit2 size={16} /> {t('growthCenter.myProjectResults')}
        </Link>
      </section>
    </main>
  )
}

function StudentGrowthCenterPage() {
  const { isLoggedIn } = useAuth()
  const { language, t } = useLanguage()
  const [state, setState] = useState({ loading: true, error: '', payload: null })
  const [todayState, setTodayState] = useState({ today: null, dashboard: null })
  const [closedLoopState, setClosedLoopState] = useState({ weekly: null, ability: null, suggestion: null, mentor: null })
  const [completingId, setCompletingId] = useState('')
  const [localPlans, setLocalPlans] = useState({ competitions: [], certificates: [], training: [] })
  const [aiGrowthPlan, setAiGrowthPlan] = useState(null)

  const loadGrowth = useCallback(() => {
    setState((current) => ({ ...current, loading: true, error: '' }))
    Promise.all([
      getStudentGrowthMe(),
      getStudentGrowthToday().catch(() => null),
      getStudentGrowthDashboard().catch(() => null),
      getGrowthEventsWeeklyReport().catch(() => null),
      getGrowthEventsAbilityScore().catch(() => null),
      getGrowthEventsTodaySuggestion().catch(() => null),
      getGrowthMentorToday().catch(() => null),
      getLatestAiGrowthPlan().catch(() => null),
    ])
      .then(([payload, today, dashboard, weekly, ability, suggestion, mentor, aiPlanPayload]) => {
        setState({ loading: false, error: '', payload })
        setTodayState({ today, dashboard })
        setClosedLoopState({ weekly, ability, suggestion, mentor })
        setAiGrowthPlan(aiPlanPayload?.plan || null)
      })
      .catch((error) => {
        setState({
          loading: false,
          error: error?.message || t('growthCenter.loadFailedTitle'),
          payload: null,
        })
      })
  }, [t])

  useEffect(() => {
    setLocalPlans({
      competitions: readLocalPlanItems('student_plan_competitions'),
      certificates: readLocalPlanItems('student_plan_certificates'),
      training: readLocalPlanItems('student_plan_training'),
    })
  }, [])

  useEffect(() => {
    if (!isLoggedIn) {
      setState({ loading: false, error: '', payload: null })
      setClosedLoopState({ weekly: null, ability: null, suggestion: null, mentor: null })
      setAiGrowthPlan(null)
      return
    }
    loadGrowth()
  }, [isLoggedIn, loadGrowth])

  const activePlan = getEffectiveGrowthPlan(state.payload)
  const hasGrowthData = hasEffectiveGrowthData(state.payload)
  const activeDirection = getCareerDirection(state.payload)
  const completedRoadmapIds = useMemo(() => getCompletedRoadmapStages(activePlan), [activePlan])
  const roadmap = useMemo(() => buildStudentRoadmap(activePlan), [activePlan])
  const isAiPlan = activePlan?.source === 'ai_generated' || Number(activePlan?.schemaVersion) >= 2
  const roadmapSummary = useMemo(() => getRoadmapSummary(roadmap, completedRoadmapIds), [roadmap, completedRoadmapIds])
  const apiTasks = useMemo(() => normalizeTasks(state.payload, t), [state.payload, t])
  const tasks = apiTasks.length ? apiTasks : buildFallbackTasks(activePlan, t)
  const doneCount = tasks.filter((task) => task.status === 'done' || task.status === 'completed').length
  const progress = tasks.length ? Math.round((doneCount / tasks.length) * 100) : 0
  const events = useMemo(() => normalizeEvents(state.payload, t), [state.payload, t])
  const fallbackLevel = Math.max(1, Math.floor((doneCount + roadmapSummary.completedCount) / 2) + 1)
  const fallbackGrowthValue = doneCount * 20 + roadmapSummary.completedCount * 80 + events.length * 10
  const growthLevel = normalizeGrowthLevel(state.payload, fallbackLevel)
  const growthValue = normalizeGrowthValue(state.payload, fallbackGrowthValue)
  const todaySummary = todayState.today?.summary || null
  const dashboard = todayState.dashboard || null
  const weeklyReport = closedLoopState.weekly || null
  const abilityScore = closedLoopState.ability?.ability || closedLoopState.suggestion?.ability || null
  const todayAdvice = closedLoopState.suggestion || null
  const mentor = closedLoopState.mentor?.mentor || null
  const mentorAdvice = mentor?.todayAdvice || null
  const mentorWeekly = mentor?.weeklyReport || null
  const mentorAbility = mentor?.ability || abilityScore || {}
  const weakPoints = Array.isArray(mentorWeekly?.weakPoints) ? mentorWeekly.weakPoints : []

  const completeTask = async (task) => {
    if (task.readonly || completingId) return
    setCompletingId(task.id)
    try {
      await completeStudentGrowthTask(task.id)
      loadGrowth()
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error?.message || t('growthCenter.loadFailedTitle'),
      }))
    } finally {
      setCompletingId('')
    }
  }

  if (!isLoggedIn || (!state.loading && !state.error && !hasGrowthData && !aiGrowthPlan)) {
    return <EmptyGrowthState />
  }

  const remaining = Math.max(
    0,
    Number(todaySummary?.totalTasks || tasks.length) - Number(todaySummary?.completedTasks || doneCount),
  )
  const currentStageName = dashboard?.currentStage?.title || roadmapSummary.current?.name || t('growthCenter.notStarted')

  return (
    <main className="page-container student-growth-center-page">
      <section className="student-growth-center-hero">
        <div>
          <span><Sparkles size={18} /> {t('growthCenter.emptyEyebrow')}</span>
          <h1>{t('growthCenter.heroTitle')}</h1>
          <p>{t('growthCenter.heroDesc')}</p>
        </div>
        <div className="student-growth-hero-actions">
          <Link className="primary-button" to="/today">
            <CalendarCheck size={16} /> {t('growthCenter.startTodayPlan')}
          </Link>
          <Link className="secondary-button" to="/explore">
            <Compass size={16} />
            {t('growthCenter.adjustGoal')} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {state.error ? (
        <section className="student-growth-error-state">
          <MessagesSquare size={20} />
          <div>
            <strong>{t('growthCenter.loadFailedTitle')}</strong>
            <p>{state.error}</p>
          </div>
          <button type="button" className="secondary-button" onClick={loadGrowth}>
            {t('growthCenter.retry')}
          </button>
          <button type="button" className="secondary-button" onClick={() => openFeedbackDialog()}>
            {t('growthCenter.submitFeedback')}
          </button>
        </section>
      ) : null}

      {aiGrowthPlan ? (
        <section className="student-growth-ai-plan" aria-labelledby="ai-growth-plan-title">
          <div className="student-growth-ai-plan-head">
            <div>
              <span><Sparkles size={18} /> {language === 'en' ? 'AI growth plan' : 'AI 成长方案'}</span>
              <h2 id="ai-growth-plan-title">{aiGrowthPlan.target}</h2>
              <p>{aiGrowthPlan.overview}</p>
            </div>
            <Link className="secondary-button" to="/onboarding">
              <Clock3 size={16} /> {language === 'en' ? 'Adjust time or goal' : '调整目标或每日时间'}
            </Link>
          </div>
          <div className="student-growth-ai-stages">
            {(aiGrowthPlan.stages || []).map((stage, index) => (
              <article key={`${stage.name}-${index}`}>
                <span>{language === 'en' ? `Stage ${index + 1}` : `阶段 ${index + 1}`}</span>
                <h3>{stage.name}</h3>
                <small>{stage.duration}</small>
                <p>{(stage.objectives || []).join(language === 'en' ? '; ' : '；')}</p>
                <ul>{(stage.tasks || []).slice(0, 4).map((task) => <li key={task}>{task}</li>)}</ul>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className="student-growth-daily-assistant" id="today">
        <div>
          <span><CalendarCheck size={18} /> {t('growthCenter.todayTasks')}</span>
          <h2>{state.loading ? t('growthCenter.loadingTodayTasks') : t('growthCenter.remainingTasks', { count: remaining })}</h2>
          <p>{todaySummary ? t('growthCenter.estimatedXp', { minutes: todaySummary.estimatedMinutes, xp: todaySummary.totalXp }) : t('growthCenter.dailyDesc')}</p>
        </div>
        <div className="student-growth-progress-ring" aria-label={t('growthCenter.progressLabel', { progress })}>
          <strong>{todaySummary?.totalTasks ? Math.round((todaySummary.completedTasks / todaySummary.totalTasks) * 100) : progress}%</strong>
          <span>{todaySummary ? `${todaySummary.completedTasks}/${todaySummary.totalTasks}` : `${doneCount}/${tasks.length || 0}`}</span>
        </div>
        <Link className="primary-button" to="/today">
          {t('growthCenter.startTodayPlan')}<ArrowRight size={16} />
        </Link>
      </section>

      <section className="ai-growth-mentor-panel">
        <header>
          <div>
            <span><Sparkles size={16} /> {t('growthMentor.eyebrow')}</span>
            <h2>{t('growthMentor.title')}</h2>
            <p>{t(`growthMentor.advice.${mentorAdvice?.key || 'restart'}`)}</p>
          </div>
          <strong>{t(mentorStatusKey(mentor?.recentStatus))}</strong>
        </header>
        <div className="ai-growth-mentor-summary">
          <article>
            <span>{t('growthMentor.currentGoal')}</span>
            <strong>{mentor?.currentGoal || dashboard?.currentPath?.name || activeDirection || t('growthMentor.noGoal')}</strong>
          </article>
          <article>
            <span>{t('growthMentor.currentStage')}</span>
            <strong>{mentor?.currentStage || currentStageName || t('growthMentor.noStage')}</strong>
          </article>
          <article>
            <span>{t('growthMentor.learningProgress')}</span>
            <strong>{mentor?.learningProgress ?? dashboard?.pathProgress ?? roadmapSummary.progress ?? 0}%</strong>
          </article>
          <article>
            <span>{t('growthMentor.recentStatus')}</span>
            <strong>{t(mentorStatusKey(mentor?.recentStatus))}</strong>
          </article>
        </div>
        <div className="ai-growth-mentor-advice">
          <div>
            <span>{t('growthMentor.todayAdvice')}</span>
            <p>{t(`growthMentor.reasons.${mentorAdvice?.reasonKey || 'no_activity'}`, { value: mentorAdvice?.reasonText || '' })}</p>
          </div>
          {(mentorAdvice?.nextActionKeys || ['continue_learning', 'finish_today_task']).slice(0, 3).map((key) => (
            <span key={key}>{t(`growthMentor.actions.${key}`)}</span>
          ))}
        </div>
        <div className="ai-growth-mentor-weekly">
          <article>
            <span>{t('growthMentor.learningTime')}</span>
            <strong>{t('growthMentor.minutes', { count: mentorWeekly?.learningTime || weeklyReport?.learningTime || 0 })}</strong>
          </article>
          <article>
            <span>{t('growthMentor.completedTasks')}</span>
            <strong>{t('growthMentor.tasks', { count: mentorWeekly?.completedTasks || weeklyReport?.completedTasks || 0 })}</strong>
          </article>
          <article>
            <span>{t('growthMentor.abilityChange')}</span>
            <strong>{t('growthMentor.score', { count: mentorAbility.total || 0 })}</strong>
          </article>
          <article>
            <span>{t('growthMentor.projectProgress')}</span>
            <strong>{mentorWeekly?.projectProgress || mentorAbility.projectProgress || 0}%</strong>
          </article>
        </div>
        <footer>
          <div>
            <strong>{t('growthMentor.weakPoints')}</strong>
            {(weakPoints.length ? weakPoints : [null]).slice(0, 3).map((item, index) => (
              <span key={item?.title || `weak-${index}`}>{item ? weakPointLabel(item, t) : t('growthMentor.weakEmpty')}</span>
            ))}
          </div>
          <p><strong>{t('growthMentor.nextWeek')}</strong>{t(`growthMentor.nextWeekAdvice.${mentorWeekly?.nextWeekSuggestionKey || 'next_week_review'}`)}</p>
        </footer>
      </section>

      <section className="growth-closed-loop-report">
        <header>
          <div>
            <span><Sparkles size={16} /> {t('growthCenter.closedLoop')}</span>
            <h2>{t('growthCenter.weeklyReport')}</h2>
            <p>{todayAdvice?.suggestion || t('growthCenter.closedLoopFallback')}</p>
          </div>
          <strong>{abilityScore?.level || 'starter'} - {abilityScore?.total || 0}</strong>
        </header>
        <div className="growth-closed-loop-grid">
          <article>
            <span>{t('growthCenter.learningTime')}</span>
            <strong>{t('growthCenter.minutes', { count: weeklyReport?.learningTime || 0 })}</strong>
            <p>{t('growthCenter.learningTimeDesc')}</p>
          </article>
          <article>
            <span>{t('growthCenter.completedTasks')}</span>
            <strong>{weeklyReport?.completedTasks || 0}</strong>
            <p>{t('growthCenter.completedTasksDesc')}</p>
          </article>
          <article>
            <span>{t('growthCenter.abilityGrowth')}</span>
            <strong>{abilityScore?.learningCompletion || 0}%</strong>
            <p>{t('growthCenter.abilityGrowthDesc')}</p>
          </article>
          <article>
            <span>{t('growthCenter.projectProgress')}</span>
            <strong>{weeklyReport?.projectProgress || abilityScore?.projectProgress || 0}%</strong>
            <p>{t('growthCenter.projectProgressDesc')}</p>
          </article>
        </div>
        <div className="growth-closed-loop-advice">
          <strong>{t('growthCenter.todayAdvice')}</strong>
          <p>{todayAdvice?.reason || t('growthCenter.noRecentActivity')}</p>
          {(todayAdvice?.nextActions || []).slice(0, 3).map((item) => <span key={item}>{item}</span>)}
        </div>
      </section>

      <section className="student-growth-dashboard-overview">
        <article>
          <span><BookOpen size={16} /> {t('growthCenter.learningOverview')}</span>
          <strong>{dashboard?.currentPath?.name || activeDirection || t('growthCenter.currentDirection')}</strong>
          <p>{t('growthCenter.routeProgress', { progress: dashboard?.pathProgress ?? roadmapSummary.progress ?? 0, stage: currentStageName })}</p>
          <div className="student-today-progress"><i style={{ width: `${dashboard?.pathProgress ?? 0}%` }} /></div>
        </article>
        <article>
          <span><Target size={16} /> {t('growthCenter.practiceAndProject')}</span>
          <strong>{dashboard?.practiceProgress ? `${dashboard.practiceProgress.completed}/${dashboard.practiceProgress.total}` : `${doneCount}/${tasks.length || 0}`}</strong>
          <p>{t('growthCenter.projectLabel', { label: dashboard?.projectProgress?.label || t('growthCenter.noActiveProject') })}</p>
          <div className="student-today-progress"><i style={{ width: `${dashboard?.practiceProgress?.percent ?? 0}%` }} /></div>
        </article>
        <article>
          <span><Sparkles size={16} /> {t('growthCenter.nextSuggestion')}</span>
          <strong>{t('growthCenter.streakDays', { count: dashboard?.streakDays || 0 })}</strong>
          <p>{dashboard?.nextSuggestion || t('growthCenter.nextSuggestionFallback')}</p>
        </article>
      </section>

      <section className="student-growth-level-strip" aria-label={t('growthCenter.levelAria')}>
        <article>
          <span>{t('growthCenter.growthLevel')}</span>
          <strong>Lv.{growthLevel}</strong>
          <p>{t('growthCenter.growthLevelDesc')}</p>
        </article>
        <article>
          <span>{t('growthCenter.growthValue')}</span>
          <strong>{growthValue}</strong>
          <p>{t('growthCenter.growthValueDesc')}</p>
        </article>
        <article>
          <span>{t('growthCenter.timelineCount')}</span>
          <strong>{events.length}</strong>
          <p>{events[0]?.title || t('growthCenter.timelineFallback')}</p>
        </article>
      </section>

      {roadmap.length ? (
        <section className="student-growth-roadmap-summary">
          <div className="student-growth-roadmap-head">
            <div>
              <span><Target size={18} /> {t('growthCenter.learningRoute')}</span>
              <h2>{roadmapSummary.progress}%</h2>
              <p>{t('growthCenter.stagesCompleted', { done: roadmapSummary.completedCount, total: roadmapSummary.total })}</p>
            </div>
            <Link className="primary-button" to="/learning">{t('growthCenter.viewFullRoute')} <ArrowRight size={16} /></Link>
          </div>
          <div className="student-growth-roadmap-current">
            <article>
              <span>{t('growthCenter.currentStage')}</span>
              <strong>{roadmapSummary.current?.name || t('growthCenter.none')}</strong>
              <p>{roadmapSummary.current?.todayTask || t('growthCenter.createPlanForRoute')}</p>
            </article>
            <article>
              <span>{t('growthCenter.nextStage')}</span>
              <strong>{roadmapSummary.next?.name || t('growthCenter.finalStage')}</strong>
              <p>{roadmapSummary.next?.goal || t('growthCenter.finalStageDesc')}</p>
            </article>
          </div>
          <div className="student-growth-roadmap-recent">
            {roadmapSummary.recent.map((stage) => {
              const step = stage.steps?.find((item) => !item.completed) || stage.steps?.[0]
              const href = step?.id
                ? `${isAiPlan ? '/learning/ai/' : '/learning/'}${encodeURIComponent(step.id)}`
                : '/learning'
              return <Link key={stage.id} to={href}>
                <span>{t('growthCenter.stageOrder', { order: stage.order })}</span>
                <strong>{stage.name}</strong>
                <p>{stage.goal}</p>
              </Link>
            })}
          </div>
        </section>
      ) : null}

      <section className="student-growth-task-list">
        {state.loading ? (
          <article className="student-growth-task-card">
            <Clock3 size={18} />
            <div>
              <strong>{t('growthCenter.loading')}</strong>
              <p>{t('growthCenter.loadingLong')}</p>
            </div>
          </article>
        ) : null}

        {!state.loading && tasks.length === 0 ? (
          <article className="student-growth-task-card">
            <Target size={18} />
            <div>
              <strong>{t('growthCenter.noTasksTitle')}</strong>
              <p>{t('growthCenter.noTasksDesc')}</p>
            </div>
            <Link className="secondary-button" to={hasGrowthData ? '/learning' : '/explore'}>
              {hasGrowthData ? t('growthCenter.viewFullRoute') : t('growthCenter.goCareerPlanner')}
            </Link>
          </article>
        ) : null}

        {!state.loading
          ? tasks.map((task) => {
              const isDone = task.status === 'done' || task.status === 'completed'
              return (
                <article className={`student-growth-task-card ${isDone ? 'is-done' : ''}`} key={task.id}>
                  <button
                    type="button"
                    className="student-growth-task-check"
                    onClick={() => completeTask(task)}
                    disabled={task.readonly || isDone || completingId === task.id}
                    aria-label={isDone ? t('growthCenter.done') : t('growthCenter.markDone')}
                  >
                    {isDone ? <CheckCircle2 size={22} /> : <Circle size={22} />}
                  </button>
                  <div>
                    <span>{taskTypeLabel(task.type, t)}</span>
                    <strong>{task.title}</strong>
                    <p>{t('growthCenter.updatedAt', { time: dateTime(task.updatedAt || task.createdAt, language) })}</p>
                  </div>
                </article>
              )
            })
          : null}
      </section>

      {(localPlans.competitions.length || localPlans.certificates.length || localPlans.training.length) ? (
        <section className="student-growth-linked-status">
          <article>
            <span>{t('growthCenter.trainingActive')}</span>
            <strong>{t('growthCenter.itemCount', { count: localPlans.training.length })}</strong>
            <p>{localPlans.training[0]?.task || t('growthCenter.trainingFallback')}</p>
          </article>
          <article>
            <span>{t('growthCenter.competitionPrep')}</span>
            <strong>{t('growthCenter.itemCount', { count: localPlans.competitions.length })}</strong>
            <p>{localPlans.competitions[0]?.task || t('growthCenter.competitionFallback')}</p>
          </article>
          <article>
            <span>{t('growthCenter.certificatePrep')}</span>
            <strong>{t('growthCenter.itemCount', { count: localPlans.certificates.length })}</strong>
            <p>{localPlans.certificates[0]?.task || t('growthCenter.certificateFallback')}</p>
          </article>
        </section>
      ) : null}

      <section className="student-growth-timeline">
        <div className="student-growth-roadmap-head">
          <div>
            <span><Clock3 size={18} /> {t('growthCenter.growthTimeline')}</span>
            <h2>{t('growthCenter.recentRecords')}</h2>
            <p>{t('growthCenter.timelineDesc')}</p>
          </div>
        </div>
        <div className="student-growth-timeline-list">
          {events.length ? events.slice(0, 5).map((event) => (
            <article key={event.id}>
              <span>{dateTime(event.createdAt, language)}</span>
              <strong>{event.title}</strong>
              <p>{event.description || event.type}</p>
            </article>
          )) : (
            <article>
              <span>{t('growthCenter.waitingFirstRecord')}</span>
              <strong>{t('growthCenter.firstRecordTitle')}</strong>
              <p>{t('growthCenter.firstRecordDesc')}</p>
            </article>
          )}
        </div>
      </section>

      <section className="student-growth-center-actions">
        <Link to="/learning"><Sparkles size={18} /> {t('growthCenter.trainingAssistant')}</Link>
        <Link to="/explore"><GraduationCap size={18} /> {t('growthCenter.majorDirections')}</Link>
        <Link to="/more/competitions"><Trophy size={18} /> {t('growthCenter.competitionPlanning')}</Link>
        <Link to="/more/certificates"><FileText size={18} /> {t('growthCenter.certificatePlanning')}</Link>
        <Link to="/projects"><FolderGit2 size={18} /> {t('growthCenter.myProjectResults')}</Link>
        <Link to="/projects"><Target size={18} /> {t('growthCenter.projectRecommendations')}</Link>
        <Link to="/more/resume"><FileText size={18} /> {t('growthCenter.resumeOptimization')}</Link>
        <Link to="/more/interview"><MessagesSquare size={18} /> {t('growthCenter.interviewPrep')}</Link>
      </section>
    </main>
  )
}

export default StudentGrowthCenterPage
